import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { UserContext } from './MiniDrawer';
import { hasPageAccess, getRoleDisplayName, ROLE_IDS } from '../utils/roleManager';

const ProtectedRoute = ({ children, requiredRole = null, requiredOrgRole = null }) => {
  const context = useContext(UserContext);
  
  // Debug logging for production
  if (process.env.NODE_ENV !== 'development') {
    console.log('🔍 ProtectedRoute Debug:', {
      hasContext: !!context,
      contextKeys: context ? Object.keys(context) : [],
      isAuthenticated: context?.isAuthenticated,
      backendUser: !!context?.backendUser,
      authLoading: context?.authLoading
    });
  }
  
  // Handle case where context is not available yet
  if (!context) {
    // In development mode, allow access even without context
    if (process.env.NODE_ENV === 'development') {
      return children;
    }
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Initializing application...</Typography>
      </Box>
    );
  }

  const { backendUser, isAuthenticated, authLoading } = context;

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading authentication...</Typography>
      </Box>
    );
  }

  // Check authentication status
  if (!isAuthenticated) {
    console.log('❌ User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // In development mode, always allow access
  if (process.env.NODE_ENV === 'development') {
    return children;
  }

  // Check if user has backend data (for role-based access)
  if (!backendUser && process.env.NODE_ENV !== 'development') {
    console.log('⚠️  User authenticated but no backend data, blocking access');
    
    // If user is on home page, allow access but show warning
    if (window.location.pathname === '/') {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              User Not Found in Database
            </Typography>
            <Typography variant="body1">
              You are authenticated with Cognito but not found in the application database.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please contact your administrator to be added to the system.
            </Typography>
          </Alert>
          {children}
        </Box>
      );
    }
    
    // For all other pages, block access
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied - User Not Found
          </Typography>
          <Typography variant="body1">
            You are authenticated with Cognito but not found in the application database.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please contact your administrator to be added to the system.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const userRole = backendUser?.role_id !== undefined && backendUser?.role_id !== null
    ? Number(backendUser.role_id)
    : undefined;
  const orgRoleId = backendUser?.org_role_id !== undefined && backendUser?.org_role_id !== null
    ? Number(backendUser.org_role_id)
    : undefined;

  // If a specific org role is required (e.g. People Manager area)
  if (requiredOrgRole !== null) {
    if (orgRoleId !== requiredOrgRole) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1">
              This page is reserved for users with hierarchical role ID <strong>{requiredOrgRole}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please contact your administrator if you believe this is an error.
            </Typography>
          </Alert>
        </Box>
      );
    }
    // Se l'utente ha superato il controllo org_role_id, permettere l'accesso
    // (non controllare hasPageAccess perché queste pagine usano org_role_id invece di role_id)
    return children;
  }

  // Admins always have access
  if (userRole === ROLE_IDS.ADMIN) {
    return children;
  }

  // Check if user has access to this page (only if we have role information)
  if (userRole && !hasPageAccess(userRole, window.location.pathname)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            You don't have permission to access this page. Your current role is: <strong>{getRoleDisplayName(userRole)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please contact your administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If specific role is required, check it (only if we have role information)
  if (requiredRole && userRole && userRole !== requiredRole) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Insufficient Permissions
          </Typography>
          <Typography variant="body1">
            This page requires the role: <strong>{getRoleDisplayName(requiredRole)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your current role is: <strong>{getRoleDisplayName(userRole)}</strong>
          </Typography>
        </Alert>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute; 